import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCreateHourlyTuitionBilling } from '~entities/payment/api/hourly-tuition/create-hourly-tuition-billing';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

import { HourlyTuitionRangeRow } from './hourly-tuition-range-row';

// Define Zod schema for validation
const rangeSchema = z.object({
  minMinute: z.number().int().min(0, 'Minimum minutes must be at least 0'),
  maxMinute: z
    .number()
    .int()
    .min(1, 'Maximum minutes must be at least 1')
    .refine((val) => val > 0, 'Maximum minutes must be greater than 0'),
  flatRate: z.number().min(0, 'Flat rate must be at least 0'),
});

const formSchema = z.object({
  name: z.string().min(1, 'Plan name is required'),
  ranges: z
    .array(rangeSchema)
    .min(1, 'At least one range is required')
    .refine(
      (ranges) => {
        // Check that ranges don't overlap and are in ascending order
        for (let i = 0; i < ranges.length - 1; i++) {
          const currentRange = ranges[i];
          const nextRange = ranges[i + 1];
          if (
            currentRange &&
            nextRange &&
            currentRange.maxMinute >= nextRange.minMinute
          ) {
            return false;
          }
        }
        return true;
      },
      { message: 'Ranges must not overlap and should be in ascending order' },
    ),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateHourlyTuitionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateHourlyTuitionForm = ({
  open,
  onOpenChange,
}: CreateHourlyTuitionFormProps) => {
  const { mutateAsync: createHourlyTuitionBilling, isPending } =
    useCreateHourlyTuitionBilling();

  const {
    register,
    control,
    handleSubmit: hookFormSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      ranges: [{ minMinute: 30, maxMinute: 60, flatRate: 10 }],
    },
    mode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ranges',
  });

  const addRange = () => {
    const lastRange = fields[fields.length - 1];
    append({
      minMinute: lastRange?.maxMinute ? lastRange.maxMinute + 1 : 1,
      maxMinute: lastRange?.maxMinute ? lastRange.maxMinute + 30 : 30,
      flatRate: 0,
    });
  };

  const onSubmit = async (data: FormValues) => {
    try {
      await createHourlyTuitionBilling(data);
      resetForm();
      onOpenChange(false);
      toast.success('Hourly tuition billing created successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to create hourly tuition billing');
    }
  };

  const resetForm = () => {
    reset({
      name: '',
      ranges: [{ minMinute: 30, maxMinute: 60, flatRate: 10 }],
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);
        if (!open) resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button variant='outline'>Create Hourly Tuition</Button>
      </DialogTrigger>
      <DialogContent className='max-h-[80vh] max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Create Hourly Tuition</DialogTitle>
          <DialogDescription>
            Set up your hourly tuition structure with minute ranges and flat
            rates.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={hookFormSubmit(onSubmit)} className='space-y-6'>
          <div className='space-y-2'>
            <Label htmlFor='name'>Plan Name</Label>
            <Input
              id='name'
              {...register('name')}
              placeholder='e.g., Standard Billing'
            />
            {errors.name && (
              <p className='mt-1 text-sm text-red-500'>{errors.name.message}</p>
            )}
          </div>

          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <Label className='text-base font-medium'>Billing Ranges</Label>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={addRange}
                className='flex items-center gap-2 bg-transparent'
              >
                <Plus className='size-4' />
                Add Range
              </Button>
            </div>

            <div className='space-y-3'>
              {fields.map((field, index) => (
                <HourlyTuitionRangeRow
                  key={field.id}
                  index={index}
                  register={register}
                  errors={errors}
                  canDelete={fields.length > 1}
                  onDelete={() => {
                    remove(index);
                  }}
                />
              ))}
            </div>
            {errors.ranges && typeof errors.ranges.message === 'string' && (
              <p className='mt-2 text-sm text-red-500'>
                {errors.ranges.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant='outline' disabled={isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button type='submit' disabled={isPending}>
              {isPending && <Loader2 className='animate-spin' />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
